import { formatTotalsCurrency, formatNumeric } from '../utils/formatters'
import type { ReimbursementsTotalsRowProps } from '../types'
import {
  FileText,
  Wallet,
  CheckCircle,
  User,
  MoreHorizontal,
  ClipboardList,
  DollarSign,
  FileSearch,
  Minus,
  X,
} from 'lucide-react'
import styles from '../styles/reimbursementTotals.module.css'
import { cn } from '@/lib/utils/helpers'

export function ReimbursementsTotalsRow({ totals, onStatusClick }: ReimbursementsTotalsRowProps) {
  return (
    <>
      <div className={styles.overviewContainer}>
        <div className={styles.overviewHeader}>
          <h2 className={styles.overviewTitle}>Reimbursement overview</h2>
          <p className={styles.overviewSubtitle}>Current portfolio summary</p>
        </div>
        <div className={styles.overviewCards}>
          <div className={cn(styles.overviewCard, styles.cardBlue)}>
            <div className={styles.overviewCardAccent}></div>
            <div className={styles.overviewCardIconWrapper}>
              <FileText className="w-6 h-6" />
            </div>
            <div className={styles.overviewCardContent}>
              <span className={styles.overviewCardLabel}>Contracts</span>
              <span className={styles.overviewCardValue}>{formatTotalsCurrency(totals.certificate_amount)}</span>
            </div>
          </div>
          <div className={cn(styles.overviewCard, styles.cardPurple)}>
            <div className={styles.overviewCardAccent}></div>
            <div className={styles.overviewCardIconWrapper}>
              <Wallet className="w-6 h-6" />
            </div>
            <div className={styles.overviewCardContent}>
              <span className={styles.overviewCardLabel}>Reimbursements</span>
              <span className={styles.overviewCardValue}>{formatTotalsCurrency(totals.default_paid)}</span>
            </div>
          </div>
          <div className={cn(styles.overviewCard, styles.cardGreen)}>
            <div className={styles.overviewCardAccent}></div>
            <div className={styles.overviewCardIconWrapper}>
              <CheckCircle className="w-6 h-6" />
            </div>
            <div className={styles.overviewCardContent}>
              <span className={styles.overviewCardLabel}>Reimbursements Paid</span>
              <span className={styles.overviewCardValue}>{formatNumeric(totals.total_default_paid)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Each card's onStatusClick args must match an entry in WORKFLOW_STATUS_FILTERS
          (constants/workflowStatusFilters.ts) so the list hook can reflect the live
          filtered count on the clicked card's badge. */}
      <div className={styles.workflowContainer}>
        <h2 className={styles.workflowTitle}>Workflow status</h2>
        <div className={styles.workflowCards}>
          
          <div
            className={cn(styles.workflowCard, styles.statusAmber, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['INFO_NEEDED'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <span className="font-serif italic font-bold" style={{ fontSize: '1.25rem', lineHeight: 1 }}>i</span>
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Info Needed</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.total_info_needed)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusBlue, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['ACTION_NEEDED'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <MoreHorizontal className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Action Needed</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.total_default_inprogress)}</span>
            </div>
          </div>
          
          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusPurple, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['AWAITING_FINAL_INFO'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <User className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Awaiting Final Info</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.total_awaiting_new_resident)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusOrange, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['READY_FOR_REVIEW'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <FileSearch className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Ready For Review</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.ready_for_review)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusIndigo, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['IN_REVIEW'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <ClipboardList className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>In Review</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.liberty_review)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusOrange, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('claim_status', ['PAYMENT_PENDING'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <DollarSign className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Payment Pending</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.payment_pending)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusRed, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['DENIED'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <Minus className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Denied</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.total_default_denied)}</span>
            </div>
          </div>

          <div className={styles.workflowSeparator}></div>

          <div
            className={cn(styles.workflowCard, styles.statusGray, onStatusClick && 'cursor-pointer hover:opacity-75 transition-opacity')}
            onClick={() => onStatusClick?.('defaultStatus', ['CANCELLED', 'EXPIRED'])}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className={styles.workflowIconWrapper}>
              <X className="w-5 h-5" />
            </div>
            <div className={styles.workflowContent}>
              <span className={styles.workflowLabel}>Cancelled/Expired</span>
              <span className={styles.workflowBadge}>{formatNumeric(totals.cancelled_expired)}</span>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}
